<template>
  <form
    @submit.prevent="submit"
    :class="['comment-input', data.isInternal ? 'comment-input--internal' : 'comment-input--external']"
  >
    <Tabs
      v-model="data.isInternal"
      :items="[{ id: false }, { id: true, disabled: !$auth.isAuthenticated || !showInternal }]"
    >
      <template #label>
        <Icon :icon="`fa-pro-regular:${data.isInternal ? 'note-sticky' : 'envelope'}`" />
        {{ data.isInternal ? $t('internalNote') : $t('sendEmail') }}
        <Icon icon="fa-pro-regular:chevron-down" />
      </template>
      <template #before>
        <ButtonGroup class="comment-input__actions">
          <template #default>
            <ButtonComponent
              type="submit"
              :icon="['fal', 'envelopes']"
              :disabled="!validator.passes"
              :label="$t('send')"
              :success-label="$t('sent')"
              :loading-label="$t('sending') + '...'"
              :promise="promise"
              @onSuccess="onSuccess"
              theme="primary"
              class="comment-input__submit"
            />
          </template>
          <template #secondary>
            <slot name="actions" />
          </template>
        </ButtonGroup>
        <InputComponent>
          <template #default>
            <textarea
              v-model="data.message"
              v-auto-height
              @keydown.enter="onKeyEnter"
              :placeholder="data.isInternal ? $t('internalComment') : $t('externalComment')"
              rows="3"
              class="elder-input__element"
              ref="input"
            ></textarea>
          </template>
          <template #below>
            <FileComponent v-model="data.attachments" :label="$tc('attachment', 2)" multiple />
            <div class="comment-input__attachments">
              <ButtonComponent
                v-for="item in data.attachments"
                :icon="activeAttachment === item ? 'trash' : `fa-pro-solid:${getAttachmentIcon(item.type)}`"
                :theme="activeAttachment === item ? 'error' : 'default'"
                :label="ExtractFilename(item.name)"
                @click="() => onAttachmentClick(item)"
              />
              <ButtonComponent icon="fa-pro-light:paperclip" :label="$t('add')" @click="onAttachmentsClick" />
            </div>
          </template>
        </InputComponent>
        <slot name="before" />
      </template>
      <template #after>
        <slot name="after" />
      </template>
    </Tabs>
  </form>
</template>

<script>
import autoHeight from '@/directives/auto-height'
import { GetAttachmentIcon } from '@/utils'
import { mapi18n } from '@/i18n'
import Model from '@/mixins/Model'
import Validator from '@/mixins/validator-v2'

import Tabs from '@/components/Tabs'
import ButtonGroup from '@kvass/button-group'
import { InputComponent } from 'vue-elder-input'
import { DropdownComponent } from 'vue-elder-dropdown'
import { FileComponent } from '@kvass/file'
import Keyboard from '@/components/Keyboard'
import { ButtonComponent } from 'vue-elder-button'

export default {
  mixins: [
    Model({
      source: undefined,
      model: {
        message: '',
        attachments: [],
        isInternal: false,
      },
    }),
    Validator({
      rules: { message: 'required' },
      labels: 'labels',
      data: 'data',
    }),
  ],
  directives: { autoHeight },
  props: {
    promise: Promise,
    showInternal: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      activeAttachment: null,
    }
  },
  computed: {
    ...mapi18n('labels', {
      message: ['message', 1],
    }),
  },
  methods: {
    ExtractFilename: filename => filename.replace(/\.[^/.]+$/, ''),
    submit() {
      if (!this.validator.passes) return
      this.$emit('submit', this.getData())
    },
    onKeyEnter(e) {
      if (!e.metaKey) return
      this.submit()
    },
    onSuccess() {
      this.data.message = ''
      this.data.attachments = []
      this.$emit('onSuccess')
    },
    onAttachmentClick(item) {
      if (!this.activeAttachment) return (this.activeAttachment = item)
      this.data.attachments = this.data.attachments.filter(a => a !== item)
      this.activeAttachment = null
    },
    onAttachmentsClick() {
      this.$el.querySelector('input[type="file"]').click()
    },
    getAttachmentIcon: GetAttachmentIcon,
    onClickAway(e) {
      if (!this.activeAttachment) return
      const activeAttachmentEl = this.$el.querySelector(`.elder-button--error`)
      if (activeAttachmentEl && !activeAttachmentEl.contains(e.target)) this.activeAttachment = null
    },
  },
  mounted() {
    document.addEventListener('click', this.onClickAway)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onClickAway)
  },
  components: {
    InputComponent,
    Tabs,
    FileComponent,
    DropdownComponent,
    Keyboard,
    ButtonGroup,
  },
}
</script>

<style lang="scss">
.comment-input {
  position: relative;

  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  overflow: clip;
  background-color: var(--bg);

  &--internal {
    --bg: #fdecaf;
    --bg-1: #{color.adjust(#fdecaf, $lightness: -8%)};
    --c-2: black;
    --bg-2: #{color.adjust(#fdecaf, $lightness: -20%)};
  }

  &--external {
    --bg: #d6e4eb;
    --bg-1: #{color.adjust(#d6e4eb, $lightness: -8%)};
    --c-2: var(--primary-contrast);
    --bg-2: var(--primary);
  }

  .tabs__details {
    background: transparent;
  }

  .elder-input {
    padding: 1rem;

    &__field {
      background: transparent;
      border: none;
    }

    &__element {
      resize: none;
      line-height: 1.42;
      padding: 0;
    }
  }

  .tabs__header {
    ul,
    li,
    a {
      border: none;
      margin: 0 !important;
    }

    li {
      & > a {
        display: inline-flex;
        align-items: center;
        justify-content: flex-start;
        gap: 0.5rem;
        padding: 0.75rem 1.25rem;
        border-bottom-right-radius: var(--border-radius);
        background-color: var(--bg-1);

        &:before {
          background: none;
        }
      }
    }

    .tabs__header--active {
      display: none;
    }
  }

  &__actions {
    position: absolute;
    top: 0;
    right: 0;

    & > .elder-button {
      border-top-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  &__submit,
  &__submit:disabled {
    border-color: var(--bg-2);
    background-color: var(--bg-2);
    color: var(--c-2);
  }

  &__attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;

    .elder-button {
      border-radius: 99px;
      font-size: 0.8em !important;
      border: 1px solid var(--vue-elder-default-border, rgba(0, 0, 0, 0.1)) !important;
      width: initial !important;

      &--default {
        background-color: transparent !important;
      }

      --vue-elder-button-padding-y: 5px;
      --vue-elder-button-padding-x: 10px;

      .elder-button__label {
        padding-left: var(--vue-elder-button-padding-x) !important;
      }
      .elder-button__icon {
        font-size: 1.25em;
        padding-right: var(--vue-elder-button-padding-x) !important;
      }
    }
  }

  .kvass-file {
    opacity: 0;
    position: absolute;
    left: -9999px;
  }
}
</style>
